import React from 'react'
import {
  Box,
  Text,
  VStack,
  Heading,
  Link,
} from '@chakra-ui/react'

import {
  CURRENT_GENETIC_CONSENT_VERSION,
} from '../../config/legalVersions'

export { CURRENT_GENETIC_CONSENT_VERSION }

interface GeneticConsentContentProps {
  /** Use compact styling for modal display (no header, less padding) */
  compact?: boolean
}

export const GeneticConsentContent: React.FC<GeneticConsentContentProps> = ({ compact = false }) => {
  return (
    <Box maxW={compact ? 'full' : '4xl'} mx="auto" p={compact ? 0 : 6}>
      <VStack gap={compact ? 4 : 6} align="stretch">
        {!compact && (
          <>
            <Box textAlign="center">
              <Heading size="3xl" fontWeight="800" mb={6}>
                Consent to Use of Your Genetic Data
              </Heading>
              <Text color="gray.600" fontSize="md">
                Version {CURRENT_GENETIC_CONSENT_VERSION} • Last updated: September 4, 2026
              </Text>
            </Box>
            <Box height="1px" bg="gray.200" my={6} />
          </>
        )}

        {compact && (
          <Text color="gray.500" fontSize="sm" mb={2}>
            Version {CURRENT_GENETIC_CONSENT_VERSION} • Last updated: September 4, 2026
          </Text>
        )}

        <Text fontSize="md" lineHeight="tall">
          Before you upload genetic data or register a DNA kit, we want to be clear about what
          Livewello does with it — and what you can ask us to do at any time. This consent is
          separate from our{' '}
          <Link href="/privacy" color="blue.500">Privacy Policy</Link> and{' '}
          <Link href="/terms" color="blue.500">Terms of Service</Link>, and separate from the
          testing consent you sign if you order a DNA kit.
        </Text>

        <Box bg="green.50" p={4} borderRadius="md">
          <Text fontSize="md" lineHeight="tall" fontWeight="700" color="green.700">
            You own your genetic data. Livewello is a custodian only.
          </Text>
        </Box>

        <Box>
          <Heading size={compact ? 'md' : 'lg'} fontWeight="700" mb={3}>
            What you are consenting to
          </Heading>
          <Text mb={3} fontSize="md" lineHeight="tall">By continuing, you consent to Livewello:</Text>
          <Box as="ol" gap={2} pl={6} fontSize="md" lineHeight="tall">
            <Box as="li" mb={2}>
              <strong>Storing</strong> your genetic data — the raw file you upload, or the file our
              laboratory partner produces from your DNA kit — in your Livewello account.
            </Box>
            <Box as="li" mb={2}>
              <strong>Analyzing</strong> it to produce the reports, insights, and genotype
              information that are the service you signed up for.
            </Box>
            <Box as="li" mb={2}>
              <strong>Processing it with AI systems</strong>, including third-party AI providers
              under contract to Livewello, so features such as Wellness Coach can answer questions
              in the context of your genetics. Direct identifiers are removed before documents are
              analyzed.
            </Box>
            <Box as="li" mb={2}>
              <strong>Retaining</strong> it for as long as your account is open, or until you
              delete it.
            </Box>
          </Box>
        </Box>

        <Box>
          <Heading size={compact ? 'md' : 'lg'} fontWeight="700" mb={3}>
            What we will never do
          </Heading>
          <Box as="ul" gap={2} pl={6} fontSize="md" lineHeight="tall">
            <Box as="li" mb={2}>
              <strong>We will never sell, rent, or otherwise commercialize your genetic data</strong>,
              including in de-identified or aggregated form. This commitment binds Livewello and any
              successor to our business.
            </Box>
            <Box as="li" mb={2}>
              <strong>We will never share it with employers, insurers, or law enforcement</strong>{' '}
              except where we are compelled by a valid court order, subpoena, or warrant.
            </Box>
            <Box as="li" mb={2}>
              <strong>We will never use it for research</strong> unless you separately opt in. This
              consent does not cover research use.
            </Box>
          </Box>
        </Box>

        <Box>
          <Heading size={compact ? 'md' : 'lg'} fontWeight="700" mb={3}>
            Your rights
          </Heading>
          <Text mb={3} fontSize="md" lineHeight="tall">
            You can exercise any of these at any time, without giving a reason:
          </Text>
          <Box as="ul" gap={2} pl={6} fontSize="md" lineHeight="tall">
            <Box as="li" mb={2}>
              <strong>Access and export</strong> — download your raw genetic file and your analysis
              results.
            </Box>
            <Box as="li" mb={2}>
              <strong>Delete</strong> — permanently remove your genetic data from Livewello&apos;s
              systems.
            </Box>
            <Box as="li" mb={2}>
              <strong>Destroy your sample</strong> (DNA kit customers) — your biological sample is
              destroyed by our laboratory partner after analysis is complete. You can also ask us to
              destroy it sooner, at any time, and we will confirm within 10 business days.
              Destroying a sample does not delete genetic data already produced from it — use the
              deletion right above for that. Our laboratory may separately retain de-identified
              material for internal quality validation, as described in the consent form you sign
              when registering your kit.
            </Box>
            <Box as="li" mb={2}>
              <strong>Withdraw this consent</strong> — in Settings, at any time. Because this
              consent is what allows us to hold and process your genetic data,{' '}
              <strong>withdrawing it permanently deletes that data</strong>: your uploaded files,
              analysis results, and genetic reports, across every profile on your account. Your
              account, your profiles, and your health records stay. This cannot be undone, and
              withdrawing does not undo processing that already happened.
            </Box>
          </Box>
        </Box>

        <Box>
          <Heading size={compact ? 'md' : 'lg'} fontWeight="700" mb={3}>
            If you are uploading data for someone else
          </Heading>
          <Text mb={3} fontSize="md" lineHeight="tall">
            Family profiles let you manage genetic data for another person, including a child. By
            uploading data for someone else, you confirm you have the legal authority to do so — as
            a parent, legal guardian, or through documented authority. If you are managing a
            child&apos;s profile, they may ask you to transfer or delete that data when they reach
            adulthood.
          </Text>
        </Box>

        <Box>
          <Heading size={compact ? 'md' : 'lg'} fontWeight="700" mb={3}>
            Questions
          </Heading>
          <Text fontSize="md" lineHeight="tall">
            <Link href="mailto:support@livewello.com" color="blue.500">support@livewello.com</Link>
            {' '}— we respond within 30 days.
          </Text>
        </Box>
      </VStack>
    </Box>
  )
}

export default GeneticConsentContent
